import React from "react";
import CustomInput from "Common/CustomInput/CustomInput";
import CustomSelect from "Common/CustomSelect/CustomSelect";
import CustomCheckbox from "Common/CustomCheckbox";
import { GENDER_OPTIONS, TODAY_DATE } from "Common/Constants";
import PropTypes from "prop-types";

const SubscriberInformation = ({
  ptModel,
  validationModel,
  handleChange,
  onPasteZip,
  planIndex = 0,
  onSameAsPatientAddressChange,
  embedded = false,
}) => {
  const getPlanData = () => {
    return ptModel.patientPlan?.[planIndex] || {};
  };

  const planData = getPlanData();
  const showSubscriberSection = planData?.relationShip && planData?.relationShip !== "18";
  const hasSameAsPatientOption = Boolean(onSameAsPatientAddressChange);
  const isSameAsPatient = hasSameAsPatientOption && (planData?.subscriberSameAsPatientAddress ?? false);

  if (!showSubscriberSection) {
    return null;
  }

  const address1Value = isSameAsPatient ? (ptModel?.address1 ?? "") : (planData?.address1 ?? "");
  const cityValue = isSameAsPatient ? (ptModel?.city ?? "") : (planData?.city ?? "");
  const stateValue = isSameAsPatient ? (ptModel?.state ?? "") : (planData?.state ?? "");
  const zipCodeValue = isSameAsPatient ? (ptModel?.zipCode ?? "") : (planData?.zipCode ?? "");

  return (
    <div className={embedded ? "" : "border rounded-lg p-4"}>
      <h3 className="text-lg font-medium text-gray-900 py-2">Subscriber Information</h3>
      <div className="space-y-4 mt-4">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-4">
              <CustomInput
                toLower
                onChange={handleChange}
                name="firstName"
                data-nested={`patientPlan.${planIndex}.firstName`}
                value={planData?.firstName}
                required
                label="First Name"
                placeholder="First Name"
              />
              {validationModel.subscriberFirstNameError}
            </div>
            <div className="col-span-12 md:col-span-4">
              <CustomInput
                toLower
                onChange={handleChange}
                name="lastName"
                data-nested={`patientPlan.${planIndex}.lastName`}
                value={planData?.lastName}
                required
                label="Last Name"
                placeholder="Last Name"
              />
              {validationModel.subscriberLastNameError}
            </div>
            <div className="col-span-12 md:col-span-4">
              <CustomInput
                onChange={handleChange}
                name="email"
                data-nested={`patientPlan.${planIndex}.email`}
                value={planData?.email}
                label="Email"
                type="email"
                autoComplete="off"
                placeholder="Email"
              />
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <CustomInput
                onChange={handleChange}
                name="dob"
                data-nested={`patientPlan.${planIndex}.dob`}
                value={planData?.dob}
                type="date"
                required
                label="DOB"
                placeholder="mm/dd/yyyy"
                max={TODAY_DATE}
              />
              {validationModel.subscriberDobError}
            </div>
            <div className="col-span-12 md:col-span-6">
              <CustomSelect
                name="gender"
                data-nested={`patientPlan.${planIndex}.gender`}
                value={planData?.gender}
                onChange={handleChange}
                required
                label="Gender"
              >
                {GENDER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </CustomSelect>
              {validationModel.subscriberGenderError}
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12">
              <div className="flex flex-col w-full">
                <div className="flex items-center justify-between gap-2 ">
                  <label className="text-secondary font-medium text-sm required">
                    Street Address
                  </label>
                  {onSameAsPatientAddressChange && (
                    <CustomCheckbox
                      label="Same as patient address"
                      name="subscriberSameAsPatientAddress"
                      checked={planData?.subscriberSameAsPatientAddress ?? false}
                      onChange={() =>
                        onSameAsPatientAddressChange(
                          !(planData?.subscriberSameAsPatientAddress ?? false),
                          planIndex
                        )
                      }
                    />
                  )}
                </div>
                <input
                  name="address1"
                  data-nested={`patientPlan.${planIndex}.address1`}
                  value={address1Value}
                  onChange={(e) => {
                    if (isSameAsPatient) return;
                    const value = (e.target?.value ?? "").trimStart();
                    const lower = value.toLowerCase();
                    handleChange({
                      target: {
                        value: lower,
                        name: "address1",
                        dataset: { nested: `patientPlan.${planIndex}.address1` },
                      },
                    });
                  }}
                  required
                  placeholder="Street Address"
                  autoComplete="off"
                  className="customInputClass h-9"
                  disabled={isSameAsPatient}
                />
              </div>
              {validationModel.subscriberAddress1Error}
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-4">
              <CustomInput
                name="city"
                data-nested={`patientPlan.${planIndex}.city`}
                value={cityValue}
                disabled
                label="City"
                placeholder="City"
                toLower
                toolTip="City and State will appear automatically once you enter a valid Zip Code."
              />
            </div>
            <div className="col-span-12 md:col-span-4">
              <CustomInput
                name="state"
                data-nested={`patientPlan.${planIndex}.state`}
                value={stateValue}
                disabled
                label="State"
                placeholder="State"
              />
            </div>
            <div className="col-span-12 md:col-span-4">
              <CustomInput
                onInput={(e) => !isSameAsPatient && onPasteZip(e, planIndex)}
                label="Zip Code"
                placeholder="Zip Code"
                name="zipCode"
                data-nested={`patientPlan.${planIndex}.zipCode`}
                onChange={handleChange}
                isNumeric 
                maxlength="9"
                value={zipCodeValue}
                required
                disabled={isSameAsPatient}
              />
              {validationModel.subscriberZipCodeError}
            </div>
          </div>
        </div>
    </div>
  );
};

SubscriberInformation.propTypes = {
  ptModel: PropTypes.object.isRequired,
  validationModel: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  onPasteZip: PropTypes.func.isRequired,
  planIndex: PropTypes.number,
  onSameAsPatientAddressChange: PropTypes.func,
  embedded: PropTypes.bool,
};

export default SubscriberInformation;
